/**
 * Turns a failed API call into the error object sent to the host page through
 * functionCallBackError.
 *
 * The result must survive window.postMessage, which structured-clones its data:
 * a raw AxiosError carries functions in its config and throws DataCloneError, so
 * the host would receive nothing. Only plain values are returned here.
 *
 * `code`, `key` and `text` keep their historical meaning so existing
 * integrations don't break. `responseCode`, `explanation` and `todoAction` are
 * added when the Payabli API returns them, so an integrator can tell a
 * deterministic rejection (for example 9005, blocked by fraud controls) apart
 * from a validation error (PIN-2589).
 *
 * Only response metadata and the error message are read — never the request
 * config/body, which holds card and bank data.
 */
export function buildApiErrorPayload(error) {
    const response = error?.response;
    if (!response?.status) {
        return {
            code: 0,
            key: error?.isAxiosError ? 'networkError' : 'clientError',
            text: error?.message || 'Unknown error',
        };
    }

    const data = response.data;
    const payload = {
        code: response.status,
        key: 'apiError',
        text: data?.responseText ? data.responseText : textFromBody(data, error),
    };

    if (data?.responseCode !== undefined && data?.responseCode !== null) {
        payload.responseCode = data.responseCode;
    }
    const explanation = data?.responseData?.explanation;
    if (explanation) {
        payload.explanation = explanation;
    }
    const todoAction = data?.responseData?.todoAction;
    if (todoAction) {
        payload.todoAction = todoAction;
    }
    return payload;
}

// Response bodies are parsed JSON or strings, so the JSON round-trip is normally
// a no-op; it guards against anything non-serializable reaching postMessage.
// An empty body falls back to the error message so `text` is never blank.
function textFromBody(data, error) {
    if (data === undefined || data === null || data === '') {
        return error?.message || 'Unknown error';
    }
    if (typeof data !== 'object') {
        return data;
    }
    try {
        return JSON.parse(JSON.stringify(data));
    } catch {
        return String(data);
    }
}
